import { Vec3 } from 'playcanvas';

import { Scene } from '../scene';

const va = new Vec3();

// numbered 2D labels for the align tool's landmark pairs: one text element
// per pair, anchored above the pair's reference point. sibling of
// DimensionLabels (which hardcodes edge-length text and a fixed count);
// here the text is caller-provided ("3" or "3 · 1,2 cm") and the count
// follows the session.
class AlignPairLabels {
    private svg: SVGSVGElement;
    private labels: SVGTextElement[] = [];

    private scene: Scene;
    private viewport: HTMLElement;

    constructor(scene: Scene, viewport: HTMLElement, parent: HTMLElement, id: string) {
        this.scene = scene;
        this.viewport = viewport;

        const ns = 'http://www.w3.org/2000/svg';
        this.svg = document.createElementNS(ns, 'svg') as SVGSVGElement;
        this.svg.classList.add('tool-svg', 'dimension-labels-svg', 'hidden');
        this.svg.id = id;
        parent.appendChild(this.svg);
    }

    setCount(count: number) {
        const ns = 'http://www.w3.org/2000/svg';
        while (this.labels.length < count) {
            const text = document.createElementNS(ns, 'text') as SVGTextElement;
            text.setAttribute('text-anchor', 'middle');
            text.setAttribute('dominant-baseline', 'middle');
            this.svg.appendChild(text);
            this.labels.push(text);
        }
        while (this.labels.length > count) {
            const text = this.labels.pop();
            text.remove();
        }
    }

    show() {
        this.svg.classList.remove('hidden');
    }

    hide() {
        this.svg.classList.add('hidden');
    }

    hideLabel(index: number) {
        this.labels[index]?.setAttribute('visibility', 'hidden');
    }

    setLabel(index: number, world: Vec3, content: string, outlier = false) {
        const label = this.labels[index];
        if (!label) {
            return;
        }
        const { camera } = this.scene;

        // hide when the anchor is behind the camera (worldToScreen mirrors
        // such positions into the viewport)
        const cameraPos = camera.mainCamera.getPosition();
        const cameraFwd = camera.mainCamera.forward;
        if (va.sub2(world, cameraPos).dot(cameraFwd) <= 0) {
            label.setAttribute('visibility', 'hidden');
            return;
        }
        label.setAttribute('visibility', 'visible');

        camera.worldToScreen(world, va);
        const x = va.x * this.viewport.clientWidth;
        const y = va.y * this.viewport.clientHeight - 16;
        label.setAttribute('transform', `translate(${x.toFixed(1)}, ${y.toFixed(1)})`);
        if (label.textContent !== content) {
            label.textContent = content;
        }
        label.classList.toggle('align-label-outlier', outlier);
    }
}

export { AlignPairLabels };
